<template>
  <div class="page-container booking-detail-page">
    <el-card class="detail-card" shadow="hover" v-loading="loading">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <el-icon class="back-icon" @click="goBack"><ArrowLeft /></el-icon>
            <h2 class="detail-title">{{ $t('booking.detail.title') }}</h2>
          </div>
        </div>
      </template>

      <div v-if="bookingData && bookingData.id" class="detail-content">
        <!-- Booking Basic Info -->
        <el-descriptions :column="2" border class="booking-descriptions">
          <el-descriptions-item :label="$t('booking.detail.orderNumber')" label-class-name="desc-label">
            <el-tag type="primary" effect="plain">{{ bookingData.orderNumber }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('booking.detail.customer')" label-class-name="desc-label">
            <div class="value-with-icon">
              <i class="iconfont icon-helmet"></i>
              <span>{{ bookingData.customerName || '-' }}</span>
            </div>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('booking.detail.teacher')" label-class-name="desc-label">
            <div class="value-with-icon">
              <i class="iconfont icon-teacher"></i>
              <span>{{ bookingData.name || '-' }}</span>
            </div>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('booking.detail.vipPackage')" label-class-name="desc-label">
            <el-tag type="warning" effect="plain">
              {{ $t('booking.detail.vipPackage') }} #{{ bookingData.vipPackageId || '-' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('booking.detail.createdAt')" label-class-name="desc-label" :span="2">
            {{ formatDateTime(bookingData.createdAt) }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- Booking Times Section -->
        <div class="booking-times-section">
          <div class="section-header">
            <el-icon class="section-icon"><Clock /></el-icon>
            <h3 class="section-title">{{ $t('booking.detail.bookingTimes') }}</h3>
          </div>
          
          <div v-if="bookingData.bookingTimes && bookingData.bookingTimes.length > 0" class="times-list">
            <div 
              v-for="bookingTime in bookingData.bookingTimes" 
              :key="bookingTime.id" 
              class="time-card"
            >
              <div class="time-card-header">
                <div class="day-info">
                  <el-tag class="weekday-badge">
                    {{ formatWeekday(bookingTime.bookingDate) }}
                  </el-tag>
                  <span class="date-text">{{ formatSimpleDate(bookingTime.bookingDate) }}</span>
                </div>
                <div class="day-actions">
                  <div class="hours-count">
                    <el-icon><Timer /></el-icon>
                    <span>{{ bookingTime.bookedHours.length }} {{ $t('booking.detail.hours') }}</span>
                  </div>
                  <!-- Cancel day button hidden - no longer needed -->
                  <!-- <el-button 
                    type="danger" 
                    size="small" 
                    :icon="Delete"
                    :loading="cancellingDay[bookingTime.id]"
                    @click="handleCancelAllDay(bookingData.id, bookingData.orderNumber, bookingTime.bookingDate, bookingTime.id)"
                    class="cancel-day-btn"
                  >
                    {{ $t('booking.detail.cancelDay') }}
                  </el-button> -->
                </div>
              </div>
              
              <div class="hours-grid">
                <div 
                  v-for="bookedHour in bookingTime.bookedHours" 
                  :key="bookedHour.id" 
                  class="hour-badge"
                >
                  <div class="hour-content">
                    <div class="hour-info">
                      <el-icon><Clock /></el-icon>
                      <span>{{ formatHour(bookedHour.hour) }}</span>
                    </div>
                    <el-tooltip :content="$t('booking.detail.cancelHour')" placement="top">
                      <el-button 
                        type="danger" 
                        size="small" 
                        :icon="Delete"
                        circle
                        @click="handleCancelHour(bookingData.id, bookedHour.hour, bookingTime.bookingDate)"
                        :loading="cancellingHours[bookedHour.id]"
                        class="cancel-hour-btn"
                      />
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <el-empty v-else :description="$t('booking.detail.noBookingTimes')" />
        </div>
      </div>

      <div v-else class="empty-state">
        <el-empty :description="$t('booking.detail.loadFailed')" />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, Clock, Timer, Delete } from '@element-plus/icons-vue'
import { getBookingDetail, cancelBooking, cancelAllBookingHours, cancelBookingHour } from '@/api/booking'
import { useBookingStore } from '@/stores/booking'
import MTime from '@/utils/time-handler'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const bookingStore = useBookingStore()
const loading = ref(false)
const bookingData = ref(null)
const cancellingHours = ref({})
const cancellingDay = ref({})

const loadBooking = async () => {
  const id = route.params.id
  if (!id) {
    ElMessage.error(t('booking.detail.bookingIdNotFound'))
    return
  }
  
  // First, check if we have cached data in the store (from list page)
  if (bookingStore.currentBooking && bookingStore.currentBooking.id === parseInt(id)) {
    bookingData.value = bookingStore.currentBooking
    console.log('[Booking Detail] Loaded from store cache:', bookingData.value)
    loading.value = false
    return
  }
  
  // If no cached data, fetch from API
  loading.value = true
  try {
    const response = await getBookingDetail(id)
    
    // Response structure: { success: true, data: bookingObject }
    if (response && response.success && response.data) {
      bookingData.value = response.data
      // Cache it in store for future use
      bookingStore.currentBooking = response.data
    } else if (response && typeof response === 'object' && response.id) {
      // Fallback: if response itself is the booking object
      bookingData.value = response
      bookingStore.currentBooking = response
    } else {
      console.error('Invalid booking data structure:', response)
      ElMessage.error(t('booking.detail.loadFailed'))
    }
  } catch (error) {
    console.error('Failed to load booking from API:', error)
    // If API fails and we don't have cached data, show error
    if (!bookingData.value) {
      ElMessage.error(t('booking.detail.loadFailed'))
    } else {
      console.warn('[Booking Detail] Using cached data from store')
    }
  } finally {
    loading.value = false
  }
}

const handleCancelAllDay = async (bookingId, orderNumber, bookingDate, bookingTimeId) => {
  try {
    await ElMessageBox.confirm(
      t('booking.detail.cancelDayConfirm', { bookingId: orderNumber }),
      t('booking.detail.cancelDayTitle', { bookingId: orderNumber }),
      {
        confirmButtonText: t('common.confirm'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )

    cancellingDay.value[bookingTimeId] = true
    
    // Use new server-side date format
    const bookingDateUTC = MTime.formatBookingDate(bookingDate)
    
    await cancelAllBookingHours(bookingId, {
      bookingId: bookingId,
      bookingDate: bookingDateUTC,
      isAdmin: true
    })

    ElMessage.success(t('booking.detail.cancelDaySuccess', { bookingId: orderNumber }))
    
    // Navigate back to previous page after successful cancellation
    setTimeout(() => {
      router.back()
    }, 500)
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to cancel all hours:', error)
      ElMessage.error(t('booking.detail.cancelDayFailed', { bookingId: orderNumber }))
    }
  } finally {
    cancellingDay.value[bookingTimeId] = false
  }
}

const handleCancelHour = async (bookingId, hour, bookingDate) => {
  try {
    await ElMessageBox.confirm(
      t('booking.detail.cancelHourConfirm', { hour: formatHour(hour), date: formatDate(bookingDate) }),
      t('booking.detail.cancelHourTitle'),
      {
        confirmButtonText: t('common.confirm'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )

    cancellingHours.value[hour] = true
    
    // Use new server-side date format
    const bookingDateUTC = MTime.formatBookingDate(bookingDate)
    
    // Use the new cancel booking hour endpoint
    const res = await cancelBookingHour(bookingId, hour, bookingDateUTC)

    if (res.success) {
      ElMessage.success(t('booking.cancelHourSuccess'))
      
      // Navigate back to previous page after successful cancellation
      setTimeout(() => {
        router.back()
      }, 500)
    } else {
      ElMessage.error(res.message || t('booking.cancelHourFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to cancel hour:', error)
      ElMessage.error(t('booking.cancelHourFailed'))
    }
  } finally {
    cancellingHours.value[hour] = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    weekday: 'short'
  })
}

const formatDayOfWeek = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  const dayOfWeek = days[date.getDay()]
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${dayOfWeek} ${year}/${month}/${day}`
}

const formatWeekday = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return days[date.getDay()]
}

const formatSimpleDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatHour = (hour) => {
  return `${hour.toString().padStart(2, '0')}:00`
}

const goBack = () => {
  router.back()
}

// Clean up when component is unmounted
onUnmounted(() => {
  // Clear the cached booking to avoid stale data
  // Only clear if we're navigating away from detail page
  if (route.path.includes('/booking/detail/')) {
    bookingStore.clearCurrentBooking()
  }
})

onMounted(() => {
  loadBooking()
})
</script>

<style scoped lang="scss">
.booking-detail-page {
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
}

.detail-card {
  border-radius: 12px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 8px;
  }
  
  :deep(.el-card__header) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px 24px;
    border-bottom: none;
    
    @media (max-width: 768px) {
      padding: 16px 20px;
    }
    
    @media (max-width: 480px) {
      padding: 14px 16px;
    }
  }
  
  :deep(.el-card__body) {
    padding: 24px;
    
    @media (max-width: 768px) {
      padding: 16px;
    }
    
    @media (max-width: 480px) {
      padding: 12px;
    }
  }
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    
    @media (max-width: 768px) {
      gap: 10px;
    }
    
    @media (max-width: 480px) {
      gap: 8px;
    }
    
    .back-icon {
      font-size: 24px;
      color: #fff;
      cursor: pointer;
      transition: all 0.3s;
      flex-shrink: 0;
      min-width: 32px;
      min-height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      @media (max-width: 768px) {
        font-size: 22px;
      }
      
      @media (max-width: 480px) {
        font-size: 20px;
      }
      
      &:hover {
        transform: translateX(-4px);
        opacity: 0.8;
      }
    }
  }
  
  .detail-title {
    color: #fff;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 18px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
}

.detail-content {
  .booking-descriptions {
    margin-bottom: 32px;
    border-radius: 8px;
    overflow: hidden;
    
    @media (max-width: 768px) {
      margin-bottom: 24px;
      border-radius: 6px;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 20px;
    }
    
    :deep(.el-descriptions__label) {
      font-weight: 600;
      background: #f5f7fa;
      width: 140px;
      
      @media (max-width: 768px) {
        width: 120px;
        font-size: 13px;
      }
      
      @media (max-width: 480px) {
        width: auto;
        display: block;
        padding: 8px 12px;
        border-right: none;
        border-bottom: 1px solid #ebeef5;
      }
    }
    
    :deep(.el-descriptions__content) {
      font-weight: 500;
      
      @media (max-width: 480px) {
        padding: 10px 12px;
      }
    }
    
    // Stack labels vertically on mobile
    @media (max-width: 480px) {
      :deep(.el-descriptions__body) {
        display: flex;
        flex-direction: column;
      }
      
      :deep(.el-descriptions__row) {
        flex-direction: column;
        display: flex;
      }
      
      :deep(.el-descriptions__cell) {
        display: block;
        width: 100% !important;
        border-bottom: 1px solid #ebeef5;
      }
    }
  }
  
  .value-with-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      gap: 6px;
    }
    
    .el-icon {
      font-size: 18px;
      color: #409eff;
      
      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
    
    span {
      word-break: break-word;
      
      @media (max-width: 480px) {
        font-size: 13px;
      }
    }
  }
}

.booking-times-section {
  margin-top: 32px;
  
  @media (max-width: 768px) {
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    margin-top: 20px;
  }
  
  .section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e4e7ed;
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
      padding-bottom: 10px;
      gap: 8px;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 14px;
      padding-bottom: 8px;
      gap: 6px;
    }
    
    .section-icon {
      font-size: 24px;
      color: #667eea;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        font-size: 22px;
      }
      
      @media (max-width: 480px) {
        font-size: 20px;
      }
    }
    
    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
      
      @media (max-width: 768px) {
        font-size: 16px;
      }
      
      @media (max-width: 480px) {
        font-size: 15px;
      }
    }
  }
  
  .times-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    
    @media (max-width: 768px) {
      gap: 12px;
    }
    
    @media (max-width: 480px) {
      gap: 10px;
    }
  }
  
  .time-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e4e7ed;
    transition: all 0.3s;
    
    @media (max-width: 768px) {
      border-radius: 10px;
      padding: 16px;
    }
    
    @media (max-width: 480px) {
      border-radius: 8px;
      padding: 14px;
    }
    
    &:hover {
      border-color: #667eea;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    }
    
    .time-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
      
      @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 14px;
      }
      
      @media (max-width: 480px) {
        margin-bottom: 12px;
        gap: 10px;
      }
      
      .day-info {
        display: flex;
        align-items: center;
        gap: 12px;
        
        @media (max-width: 768px) {
          gap: 10px;
          flex-wrap: wrap;
        }
        
        @media (max-width: 480px) {
          gap: 8px;
        }
        
        .weekday-badge {
          background: #f5a623;
          color: #fff;
          border-color: #f5a623;
          font-weight: 600;
          font-size: 14px;
          padding: 4px 12px;
          
          @media (max-width: 768px) {
            font-size: 13px;
            padding: 3px 10px;
          }
          
          @media (max-width: 480px) {
            font-size: 12px;
            padding: 3px 8px;
          }
        }
        
        .date-text {
          font-size: 15px;
          color: #303133;
          font-weight: 600;
          
          @media (max-width: 768px) {
            font-size: 14px;
          }
          
          @media (max-width: 480px) {
            font-size: 13px;
          }
        }
      }
      
      .day-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        
        @media (max-width: 768px) {
          width: 100%;
          justify-content: space-between;
          gap: 10px;
        }
        
        @media (max-width: 480px) {
          gap: 8px;
        }
        
        .hours-count {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: #909399;
          font-weight: 500;
          
          @media (max-width: 768px) {
            font-size: 13px;
          }
          
          @media (max-width: 480px) {
            font-size: 12px;
          }
          
          .el-icon {
            font-size: 16px;
            
            @media (max-width: 768px) {
              font-size: 15px;
            }
          }
        }
        
        .cancel-day-btn {
          font-size: 12px;
          padding: 6px 12px;
          
          @media (max-width: 768px) {
            font-size: 13px;
            padding: 8px 14px;
            min-height: 36px;
          }
          
          @media (max-width: 480px) {
            font-size: 12px;
            padding: 7px 12px;
            min-height: 34px;
          }
        }
      }
    }
    
    .hours-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 12px;
      
      @media (max-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 10px;
      }
      
      @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 8px;
      }
      
      .hour-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #fff;
        border-radius: 8px;
        padding: 12px 16px;
        transition: all 0.3s;
        
        @media (max-width: 768px) {
          border-radius: 6px;
          padding: 10px 14px;
        }
        
        @media (max-width: 480px) {
          border-radius: 6px;
          padding: 10px 12px;
        }
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
        }
        
        .hour-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          
          @media (max-width: 768px) {
            gap: 10px;
          }
          
          @media (max-width: 480px) {
            gap: 8px;
          }
          
          .hour-info {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            font-size: 15px;
            flex: 1;
            min-width: 0;
            
            @media (max-width: 768px) {
              font-size: 14px;
              gap: 6px;
            }
            
            @media (max-width: 480px) {
              font-size: 13px;
              gap: 5px;
            }
            
            .el-icon {
              font-size: 18px;
              flex-shrink: 0;
              
              @media (max-width: 768px) {
                font-size: 16px;
              }
              
              @media (max-width: 480px) {
                font-size: 15px;
              }
            }
            
            span {
              font-family: 'Courier New', monospace;
              letter-spacing: 1px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              
              @media (max-width: 480px) {
                letter-spacing: 0.5px;
              }
            }
          }
          
          .cancel-hour-btn {
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: #fff;
            padding: 0;
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            min-width: 28px;
            min-height: 28px;
            
            @media (max-width: 768px) {
              width: 32px;
              height: 32px;
              min-width: 32px;
              min-height: 32px;
            }
            
            @media (max-width: 480px) {
              width: 36px;
              height: 36px;
              min-width: 36px;
              min-height: 36px;
            }
            
            &:hover {
              background: rgba(245, 108, 108, 0.8);
              border-color: #f56c6c;
            }
            
            :deep(.el-icon) {
              font-size: 14px;
              
              @media (max-width: 768px) {
                font-size: 15px;
              }
              
              @media (max-width: 480px) {
                font-size: 16px;
              }
            }
          }
        }
      }
    }
  }
}

.empty-state {
  padding: 60px 0;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
  
  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

// Additional responsive styles for Element Plus components
:deep(.el-tag) {
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
}

:deep(.el-empty) {
  @media (max-width: 768px) {
    padding: 30px 0;
  }
  
  @media (max-width: 480px) {
    padding: 20px 0;
  }
}
</style>
